<script setup lang="ts">
import axios from 'axios'

import { Loader2 } from 'lucide-vue-next'
import useWebappTabs from './composables/webappPageTabs'
import AppearanceTab from './components/AppearanceTab.vue'
import TextsTab from './components/TextsTab.vue'
import ConfigTab from './components/ConfigTab.vue'
import MissionsTab from './components/MissionsTab.vue'
import PreviewTab from './components/PreviewTab.vue'
import { PREVIEW_ZONE_KEY } from './composables/previewZone'
import type { AppearanceTab as PreviewZoneName } from './composables/webappPageTabs'
import type { Audience, ImageFile, LoyaltyProgram, MerchantTexts, Reward } from '@/types/types'

const notification = useNotification()
const { t } = useI18n()
const { tabs, currentTab, currentAppearanceTab } = useWebappTabs()
const activePreviewBlock = ref<string | null>(null)
provide(PREVIEW_ZONE_KEY, { zone: currentAppearanceTab, activeBlock: activePreviewBlock })

watch(currentTab, () => {
  activePreviewBlock.value = null
})

const pickPreviewScreen = (zone: PreviewZoneName) => {
  currentAppearanceTab.value = zone
  activePreviewBlock.value = null
}
const { getMerchantConfigs, merchantConfigs } = useMerchantConfigs()
const { fillWebappRegistrationTexts, getMerchantTexts, patchWebappTexts, merchantTextsForm } = useMerchantTexts()
const { fetchMerchantRewards } = useReward()
const { saveWebapp, getWebapp } = useWebapp()
const { missions, missionsError, getMissions, saveMissions } = useWebappMissions()
const { user, merchantLogo, stores } = useLocalStorage()
const { fetchMerchantAudiences } = useAudience()

const merchantTexts = ref<MerchantTexts>(null)
const deletedImages = ref<number[]>([])
const appearanceTab = ref<InstanceType<typeof AppearanceTab> | null>(null)
const isLoading = ref(false)
const isSaving = ref(false)

const firstStoreName = computed(() => {
  if (stores.value.length > 0) {
    return stores.value[0].name
  }

  return 'Store'
})
const pageTitle = computed(() => ({
  title: t('pages.loyaltySupports.webapp.title')
}))

const emptyLoyaltyProgram: LoyaltyProgram = {
  cguUrl: '',
  cguWording: '',
  id: 0,
  privacyPolicyUrl: ''
}
const loyaltyProgram = ref<LoyaltyProgram>({ ...emptyLoyaltyProgram })
const initialLoyaltyProgram = ref<LoyaltyProgram>({ ...emptyLoyaltyProgram })
const getLoyaltyProgram = async () => {
  try {
    const { data } = await axios.get(`/merchants/${user.value.merchant.id}/loyalty-program`)
    loyaltyProgram.value = {
      cguUrl: data.data.cguUrl ?? '',
      cguWording: data.data.cguWording ?? '',
      id: data.data.id,
      privacyPolicyUrl: data.data.privacyPolicyUrl ?? ''
    }
    initialLoyaltyProgram.value = { ...loyaltyProgram.value }
  } catch {
    notification.create('error', {
      text: t('pages.loyaltyPage.tabs.settings.errors.cguLinkUpdate'),
      persist: true,
      timeout: 4000
    })
  }
}

const loyaltyProgramPayload = computed(() => {
  const payload: Record<string, string> = {}
  if (loyaltyProgram.value.cguUrl !== initialLoyaltyProgram.value.cguUrl) {
    payload.cgu_url = loyaltyProgram.value.cguUrl
  }
  if (loyaltyProgram.value.privacyPolicyUrl !== initialLoyaltyProgram.value.privacyPolicyUrl) {
    payload.privacy_policy_url = loyaltyProgram.value.privacyPolicyUrl
  }
  if (loyaltyProgram.value.cguWording !== initialLoyaltyProgram.value.cguWording) {
    payload.cgu_wording = loyaltyProgram.value.cguWording
  }

  return payload
})

const updateLoyaltyProgram = async () => {
  try {
    await axios.patch(
      `/merchants/${user.value.merchant.id}/loyalty-program/${loyaltyProgram.value.id}`,
      loyaltyProgramPayload.value
    )
    initialLoyaltyProgram.value = { ...loyaltyProgram.value }
  } catch {
    notification.create('error', {
      text: t('pages.loyaltyPage.tabs.settings.errors.cguLinkUpdate'),
      persist: true,
      timeout: 4000
    })
  }
}

const webappImages = ref<ImageFile[]>([])
const webapp = ref({
  color: '#000000',
  hasFormCompletion: false,
  id: 0,
  images: [],
  link: ''
})
const getWebappData = async () => {
  try {
    const dataWebapp = await getWebapp()
    if (dataWebapp) {
      webapp.value = dataWebapp
      customerStatus.value = {
        isEnabled: dataWebapp.hasCustomerStatus ?? false,
        audienceIds: dataWebapp.statusSegmentIds ?? []
      }
    }
  } catch (error) {
    console.error(error)
  }
}

const rewards = ref<Reward[]>([])
const getRewards = async () => {
  const rewardsData = await fetchMerchantRewards()
  rewards.value = rewardsData.data.map((reward: any) => ({
    ...reward,
    storeIds: null,
    stores: [],
    rewardType: null
  }))
}

const customerStatus = ref<{ isEnabled: boolean; audienceIds: number[] }>({ isEnabled: false, audienceIds: [] })

const audiences = ref<Audience[]>([])
const getAudiences = async () => {
  try {
    const response = await fetchMerchantAudiences({ pagination: { pageSize: 1000, page: 1 } })
    audiences.value = response?.data ?? []
  } catch {
    audiences.value = []
  }
}

const previewStatusName = computed(() => {
  if (!customerStatus.value.isEnabled) return undefined
  const firstAudienceId = customerStatus.value.audienceIds[0]

  return audiences.value.find((audience) => audience.id === firstAudienceId)?.name
})

watch(
  () => customerStatus.value.isEnabled,
  (isEnabled) => {
    if (isLoading.value || !isEnabled || currentTab.value !== 'config') return

    currentAppearanceTab.value = 'loyalty'
  }
)

const resetImages = () => {
  appearanceTab.value?.resetImages()
}

const deleteImage = async (webappImageId: number) => {
  try {
    await axios.delete(`/webapp-images/${webappImageId}`)
    deletedImages.value = deletedImages.value.filter((id) => id !== webappImageId)
  } catch (error) {
    console.error(error)
  }
}

const deleteImages = async () => {
  for (const id of deletedImages.value) {
    await deleteImage(id)
  }
}

// Show the sign-up step the toggle just added (profile form) or went back to (store selection)
const toggleFormCompletion = (value: boolean) => {
  webapp.value.hasFormCompletion = value
  currentAppearanceTab.value = value ? 'profileInfo' : 'storeSelection'
}

const submitWebapp = async () => {
  try {
    isSaving.value = true
    if (deletedImages.value.length > 0) await deleteImages()
    if (Object.keys(loyaltyProgramPayload.value).length > 0) await updateLoyaltyProgram()
    if (currentTab.value === 'texts') await patchWebappTexts()
    if (currentTab.value === 'missions' && webapp.value.id && !missionsError.value && missions.value.length) {
      await saveMissions(webapp.value.id)
    }

    const payload = {
      id: webapp.value.id,
      color: webapp.value.color,
      hasFormCompletion: webapp.value.hasFormCompletion,
      hasCustomerStatus: customerStatus.value.isEnabled,
      statusSegmentIds: customerStatus.value.isEnabled ? customerStatus.value.audienceIds : []
    }

    if (webappImages.value.length === 1) {
      await saveWebapp({
        ...payload,
        image: webappImages.value[0]
      })
    } else if (webappImages.value.length > 1) {
      // Manage multiple images to avoid error with the API block to 10MB
      for (const image of webappImages.value) {
        await saveWebapp({
          ...payload,
          image: image
        })
      }
    } else {
      await saveWebapp(payload)
    }

    webappImages.value = []
    resetImages()

    notification.create('success', {
      text: t('pages.loyaltySupports.webapp.notifications.save.success'),
      timeout: 3000
    })
    await getWebappData()
  } catch (error) {
    notification.create('error', {
      text: t('pages.loyaltySupports.webapp.notifications.save.error', {
        error: JSON.stringify(error?.response?.data)
      }),
      timeout: 3000
    })
  } finally {
    isSaving.value = false
  }
}

const submitMissions = async () => {
  if (!webapp.value.id || missionsError.value || !missions.value.length) return

  try {
    isSaving.value = true
    await saveMissions(webapp.value.id)
    notification.create('success', {
      text: t('pages.loyaltySupports.webapp.notifications.save.success'),
      timeout: 3000
    })
  } catch (error) {
    notification.create('error', {
      text: t('pages.loyaltySupports.webapp.notifications.save.error', {
        error: JSON.stringify(error?.response?.data)
      }),
      timeout: 3000
    })
  } finally {
    isSaving.value = false
  }
}

const previewMissions = computed(() =>
  missions.value
    .filter((mission) => mission.isVisible)
    .map((mission) => ({
      id: mission.id,
      type: mission.type,
      title: mission.title,
      subtitle: mission.subtitle,
      ctaLabel: mission.ctaLabel,
      iconType: mission.iconType,
      iconValue: mission.iconValue,
      points: mission.points,
      action: { destinationUrl: mission.destinationUrl ?? undefined }
    }))
)

const webappURL = computed(() => {
  return import.meta.env.VITE_ENV === 'local' ? `http://localhost:3004/${user.value.merchant.slug}` : webapp.value.link
})
onMounted(async () => {
  isLoading.value = true
  try {
    await Promise.all([
      getMerchantTexts().then((data) => {
        merchantTextsForm.value.webappTextsFields.webappHomeTitle.value = data.webappHomeTitle
        merchantTextsForm.value.webappTextsFields.webappHomeSubtitle.value = data.webappHomeSubtitle
        merchantTextsForm.value.languageTone.usePoliteLanguage.value = data.usePoliteLanguage
        fillWebappRegistrationTexts(data)
        merchantTexts.value = data
      }),
      getMerchantConfigs(),
      getLoyaltyProgram(),
      getWebappData(),
      getRewards(),
      getAudiences()
    ])
    if (webapp.value.id) await getMissions(webapp.value.id)
  } finally {
    isLoading.value = false
  }
})

const registrationTexts = computed(() =>
  Object.fromEntries(
    Object.entries(merchantTextsForm.value.webappRegistrationTextsFields).map(([key, field]) => [
      key,
      field.value?.trim() === field.defaultValue ? '' : (field.value ?? '')
    ])
  )
)

const renderImages = computed(() => {
  if (deletedImages.value.length > 0) {
    return [...webappImages.value, ...webapp.value.images].filter((image) => !deletedImages.value.includes(image.id))
  }

  return [...webappImages.value, ...webapp.value.images]
})
</script>

<template>
  <TheLoadingPage v-if="isLoading" />
  <div v-else>
    <TheListHeader :title="pageTitle.title">
      <template #actions>
        <Button variant="default-primary" :disabled="isSaving" @click="submitWebapp">
          <Loader2 v-if="isSaving" class="animate-spin" />
          {{ t('pages.loyaltySupports.webapp.buttons.save') }}
        </Button>
      </template>
    </TheListHeader>
    <div class="mb-4 px-6 sm:px-12">
      <BaseTab v-model="currentTab" :tabs="tabs" />
    </div>
    <div class="divide-border flex flex-col gap-5 pb-6 sm:pb-12 px-6 sm:px-12 xl:flex-row xl:items-start">
      <div class="min-w-0 flex-1">
        <AppearanceTab
          v-if="currentTab === 'appearance'"
          ref="appearanceTab"
          v-model:color="webapp.color"
          v-model:images="webappImages"
          v-model:deleted-images="deletedImages"
          :initial-images="webapp.images"
          :share-link="user.merchant.webAppUrl"
        />
        <TextsTab v-if="currentTab === 'texts'" :merchant-text="merchantTextsForm" />
        <MissionsTab
          v-if="currentTab === 'missions'"
          v-model="missions"
          :has-error="missionsError"
          :color="webapp.color"
          :points-label="merchantTexts?.pointsLabel"
          @save="submitMissions"
        />
        <ConfigTab
          v-if="currentTab === 'config'"
          v-model:loyalty-program="loyaltyProgram"
          v-model:is-customer-status-enabled="customerStatus.isEnabled"
          v-model:customer-status-audience-ids="customerStatus.audienceIds"
          :audiences="audiences"
          :has-webapp-force-info-completion-at-registrer="webapp.hasFormCompletion"
          @update:has-webapp-force-info-completion-at-registrer="toggleFormCompletion"
        />
      </div>
      <div class="xl:sticky xl:top-6">
        <PreviewTab
          :current-appearance-tab="currentAppearanceTab"
          :baseUrl="webappURL"
          :cgu-url="loyaltyProgram.cguUrl"
          :cgu-wording="loyaltyProgram.cguWording"
          :privacy-policy-url="loyaltyProgram.privacyPolicyUrl"
          :checkin-points="Number(merchantConfigs?.checkinPoints)"
          :has-points-by-currency="merchantConfigs?.hasPointsByCurrency"
          :loyalty-points-by-currency-unit="merchantConfigs?.loyaltyPointsByCurrencyUnit"
          :missions="previewMissions"
          :current-tab="currentTab"
          :has-form-completion="webapp.hasFormCompletion"
          :registration-texts="registrationTexts"
          :images="renderImages"
          :logo="merchantLogo"
          :merchant="user.merchant"
          :points-label="merchantTexts?.pointsLabel"
          :primary-color="webapp.color"
          :rewards="rewards"
          :slug="user.merchant.slug"
          :status-name="previewStatusName"
          :store-name="firstStoreName"
          :subtitle="merchantTextsForm.webappTextsFields.webappHomeSubtitle.value"
          :title="merchantTextsForm.webappTextsFields.webappHomeTitle.value"
          :use-polite-language="merchantTextsForm.languageTone.usePoliteLanguage.value"
          @update:current-appearance-tab="pickPreviewScreen"
        />
      </div>
    </div>
  </div>
</template>
